import { make, Payload } from "vuex-pathify"
import _ from "lodash"

import AudiencesAPI from "@/api/audiences"

import { MODEL_TEMPLATE } from "./defaults"
import * as audienceActions from "@/store/modules/audiences/actions"
import * as audienceMutations from "@/store/modules/audiences/mutations"
import * as targetingActions from "@/store/modules/targeting/actions"
import * as targetingMutations from "@/store/modules/targeting/mutations"
import {
  SET_MODEL,
  SET_MODEL_CHANGED,
  SET_MODEL_AUDIENCE,
} from "@/store/shared/mutations"

import {
  modelTextArrayAppend,
  modelTextArrayRemove,
  modelTextArrayUpdate,
} from "@/store/shared/actions"

const audienceDraftModelFromResponse = function(response) {
  return {
    id: response.id,
    adAccountId: response.adAccountId,
    ...response.draftData,
  }
}

const state = () => {
  return {
    audiences: [],
    audienceDrafts: [],
    lookalikeSources: [],
    model: {},
    loading: false,
  }
}

const actions = {
  modelTextArrayAppend,
  modelTextArrayRemove,
  modelTextArrayUpdate,
  ...audienceActions,
  ...targetingActions,

  clearModule({ commit }) {
    commit("CLEAR_MODULE")
  },

  clearAudienceModel({ commit, rootGetters }) {
    let model = _.cloneDeep(MODEL_TEMPLATE)

    model.adAccountId = rootGetters["accounts/adAccountId"]

    commit("SET_MODEL", model)
    commit("SET_MODEL_CHANGED", false)
  },

  loadAudiences({ commit, rootGetters, state }, force = false) {
    if (state.audiences.length && !force) {
      return
    }

    commit("SET_LOADING", true)

    const adAccountId = rootGetters["accounts/adAccountId"]

    AudiencesAPI.getAdAccountAudiences(adAccountId)
      .then(response => {
        commit("SET_AUDIENCES", response)
      })
      .finally(() => {
        commit("SET_LOADING", false)
      })
  },

  loadAudienceDrafts({ commit, rootGetters, state }, force = false) {
    if (state.audienceDrafts.length && !force) {
      return
    }

    commit("SET_LOADING", true)

    const adAccountId = rootGetters["accounts/adAccountId"]

    AudiencesAPI.getAdAccountAudienceDrafts(adAccountId)
      .then(response => {
        commit("SET_AUDIENCE_DRAFTS", response)
      })
      .finally(() => {
        commit("SET_LOADING", false)
      })
  },

  loadLookalikeSources({ commit, rootGetters }) {
    commit("SET_LOADING", true)

    const adAccountId = rootGetters["accounts/adAccountId"]

    AudiencesAPI.getLookalikeAudienceSources(adAccountId)
      .then(response => {
        commit("SET_LOOKALIKE_SOURCES", response)
      })
      .finally(() => {
        commit("SET_LOADING", false)
      })
  },

  saveAudienceDraft({ commit, rootGetters, state }, sourceModel = null) {
    let data = null
    let id = null

    if (!sourceModel) {
      data = {
        adAccountId: state.model.audience.adAccountId,
        draftData: { ...state.model.audience },
      }
      id = state.model.audience.id
    } else {
      data = {
        adAccountId: sourceModel.audience.adAccountId,
        draftData: { ...sourceModel.audience },
      }
      id = sourceModel.audience.id
    }

    if (!data.adAccountId) {
      data.adAccountId = rootGetters["accounts/adAccountId"]
    }

    let dataClone = _.cloneDeep(data)
    delete dataClone.draftData.id
    delete dataClone.draftData.adAccountId
    delete dataClone.draftData._changed
    delete dataClone.draftData.isPublished

    if (!id) {
      return AudiencesAPI.postAudienceDraft(dataClone).then(response => {
        let modelData = audienceDraftModelFromResponse(response)

        if (!sourceModel) {
          commit("SET_MODEL", new Payload(null, "audience", modelData))
          commit("SET_MODEL_CHANGED", false)
        }

        return modelData
      })
    } else {
      return AudiencesAPI.patchAudienceDraft(id, dataClone).then(response => {
        let modelData = audienceDraftModelFromResponse(response)

        if (!sourceModel) {
          commit("SET_MODEL", new Payload(null, "audience", modelData))
          commit("SET_MODEL_CHANGED", false)
        }

        return modelData
      })
    }
  },
}

const getters = {
  ...make.getters(state),

  modelChanged(state) {
    return state.model._changed
  },
}

const mutations = {
  ...make.mutations(state),
  ...audienceMutations,
  ...targetingMutations,
  SET_MODEL,
  SET_MODEL_CHANGED,
  SET_MODEL_AUDIENCE,
  CLEAR_MODULE(state) {
    state.audiences = []
    state.audienceDrafts = []
    state.lookalikeSources = []
    state.model = {}
    state.loading = false
  },
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}
